// src/components/Dashboard/DashboardContent.jsx
import React from "react";
import StatCard from "./StatCard";
import ProductCard from "./ProductCard";
import DashboardFilters from "./DashboardFilters";
import {
  Users,
  ShoppingCart,
  DollarSign,
  Package,
  UserX,
  FileText,
} from "lucide-react";
import CustomerList from "../Customers/CustomerList";
import OrderList from "../Orders/OrderList";
import InvoiceList from "../Invoices/InvoiceList";
import CondicaoComercialViewer from "../CondicaoComercial/CondicaoComercialViewer";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
} from "recharts";

const COLORS = {
  primary: "#ef4444",
  secondary: "#22c55e",
  accent: "#3b82f6",
  neutral: "#6b7280",
  purple: "#8b5cf6",
  orange: "#f97316",
};

const DashboardContent = ({
  currentView,
  stats,
  salesData,
  productData,
  isMobile,
  funcao,
  gerenteId,
  startDate,
  endDate,
  selectedRepresentative,
  setStartDate,
  setEndDate,
  setSelectedRepresentative,
}) => {
  const renderDashboardHome = () => {
    const maxQuantity = productData.length > 0 ? productData[0].quantity : 0;

    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Filtros */}
        <DashboardFilters
          isMobile={isMobile}
          funcao={funcao}
          gerenteId={gerenteId}
          startDate={startDate}
          endDate={endDate}
          selectedRepresentative={selectedRepresentative}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          setSelectedRepresentative={setSelectedRepresentative}
        />

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          <StatCard
            title="Total Clientes"
            value={stats.totalCustomers.toLocaleString()}
            icon={Users}
            color="red"
          />
          <StatCard
            title="Total Pedidos"
            value={stats.totalOrders.toLocaleString()}
            icon={ShoppingCart}
            color="green"
          />
          <StatCard
            title="Receita Total"
            value={`R$ ${stats.totalRevenue.toLocaleString()}`}
            icon={DollarSign}
            color="blue"
          />
          <StatCard
            title="Produtos"
            value={stats.totalProducts.toLocaleString()}
            icon={Package}
            color="yellow"
          />
          <StatCard
            title="Total Inativos"
            value={stats.totalInactiveCustomers.toLocaleString()}
            icon={UserX}
            color="purple"
          />
          <StatCard
            title="Total Orçamentos"
            value={stats.totalValidQuotes.toLocaleString()}
            icon={FileText}
            color="orange"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-xl shadow-sm">
            <h3 className="text-base sm:text-lg font-semibold mb-4">
              Tendência de Receita e Pedidos
            </h3>
            <div className="h-60 sm:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={salesData}
                  margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="revenue" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={COLORS.primary}
                        stopOpacity={0.1}
                      />
                      <stop
                        offset="95%"
                        stopColor={COLORS.primary}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    interval={isMobile ? 1 : 0}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    width={isMobile ? 45 : 60}
                    tickFormatter={(value) =>
                      new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(value)
                    }
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    width={isMobile ? 30 : 40}
                    tickFormatter={(value) =>
                      new Intl.NumberFormat("pt-BR").format(value)
                    }
                  />
                  <Tooltip
                    formatter={(value) =>
                      new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(value)
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12 }} />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    stroke={COLORS.primary}
                    fillOpacity={1}
                    fill="url(#revenue)"
                    name="Receita"
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="orders"
                    fill={COLORS.secondary}
                    opacity={0.8}
                    name="Pedidos"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm">
            <h3 className="text-base sm:text-lg font-semibold mb-4">
              Produtos Mais Vendidos
            </h3>
            <div className="space-y-2">
              {productData.map((product) => (
                <ProductCard
                  key={product.code}
                  product={product}
                  maxQuantity={maxQuantity}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (currentView) {
      case "customers":
        return <CustomerList />;
      case "orders":
        return <OrderList />;
      case "invoices":
        return <InvoiceList />;
      case "CondicaoComercial":
        return <CondicaoComercialViewer />;
      case "home":
      default:
        return renderDashboardHome();
    }
  };

  return <div className="container mx-auto">{renderContent()}</div>;
};

export default DashboardContent;
